import {
  Box,
  ButtonGroup,
  Center,
  HStack,
  Heading,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
  multiFactor,
  signInWithCustomToken,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import { compose } from "lodash/fp";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { show } from "redux-modal";
import { api } from "src/api";
import {
  ClientPermission,
  CostBasisAlgorithmEnum,
  CountryEnum,
  CurrencyCodeEnum,
  MutationUpdateClientArgs,
} from "src/api/generated/types";
import { Select } from "src/components";
import { FiscalYearModal } from "src/components/modals/FiscalYearModal";
import { OrgMemberModal } from "src/components/modals/OrgMemberModal";
import { ActionSheet, Button, Info } from "src/components/styled";
import StatusTag from "src/components/styled/StatusTag";
import WhiteBox from "src/components/styled/WhiteBox";
import { Maybe, hasValue } from "src/core";
import { useClientById, useMe, useMyToast } from "src/hooks";
import { useTheme } from "src/hooks/useTheme";
import { colors } from "src/theme";
import { auth } from "src/utils/firebase";
import Loading from "src/views/Loading";
import { Member } from "./Member";

// US = "US",
// Oceania
// NZ = "NZ", // New Zealand, NZD, FIFO
// AU = "AU", // Australia, AUD, FIFO
// europe
// DE = "DE", // germany, FIFO or LIFO
// AT = "AT", // Austria, FIFO only
// BE = "BE", // Belgium, FIFO only
// FI = "FI", // Finland, FIFO only
// FR = "FR", // France, FIFO only
// GR = "GR", // Greece, FIFO only
// IE = "IE", // Ireland, FIFO only
// IT = "IT", // Italy, FIFO or LIFO
// NL = "NL", // Netherlands, FIFO only
// NO = "NO", // Norway, FIFO only, NOK
// PL = "PL", // Poland, FIFO only
// ES = "ES", // Spain, FIFO only
// ZA = "ZA", // South Africa, FIFO or LIFO only, ZAR
// not supported
// UK = "UK",
// CA = "CA",

export const COUNTRIES = [
  {
    label: "United States 🇺🇸",
    value: CountryEnum.Us,
  },
  // Oceania
  {
    label: "Australia 🇦🇺",
    value: CountryEnum.Au,
  },
  {
    label: "New Zealand 🇳🇿",
    value: CountryEnum.Nz,
  },
  // Europea
  {
    label: "Germany 🇩🇪",
    value: CountryEnum.De,
  },
  {
    label: "Austria 🇦🇹",
    value: CountryEnum.At,
  },
  {
    label: "Belgium 🇧🇪",
    value: CountryEnum.Be,
  },
  {
    label: "Finland 🇫🇮",
    value: CountryEnum.Fi,
  },
  {
    label: "France 🇫🇷",
    value: CountryEnum.Fr,
  },
  {
    label: "Greece 🇬🇷",
    value: CountryEnum.Gr,
  },
  {
    label: "Ireland 🇮🇪",
    value: CountryEnum.Ie,
  },
  {
    label: "Italy 🇮🇹",
    value: CountryEnum.It,
  },
  {
    label: "Netherlands 🇳🇱",
    value: CountryEnum.Nl,
  },
  {
    label: "Norway 🇳🇴",
    value: CountryEnum.No,
  },
  // {
  //   label: "Sweden 🇸🇪",
  //   value: CountryEnum.Se,
  // },
  {
    label: "Denmark 🇩🇰",
    value: CountryEnum.Dk,
  },
  // {
  //   label: "Iceland 🇮🇸",
  //   value: CountryEnum.Is,
  // },
  {
    label: "Spain 🇪🇸",
    value: CountryEnum.Es,
  },
  {
    label: "Portugal 🇵🇹",
    value: CountryEnum.Pt,
  },
  // Africa
  {
    label: "South Africa 🇿🇦",
    value: CountryEnum.Za,
  },
  {
    label: "United Kingdom 🇬🇧 (Beta)",
    value: CountryEnum.Uk,
  },
  {
    label: "Canada 🇨🇦",
    value: CountryEnum.Ca,
  },
];

const CURRENCIES = [
  { value: CurrencyCodeEnum.Aud, label: "A$ AUD" },
  { value: CurrencyCodeEnum.Cad, label: "CA$ CAD" },
  { value: CurrencyCodeEnum.Eur, label: "€ EUR" },
  { value: CurrencyCodeEnum.Gbp, label: "£ GBP" },
  { value: CurrencyCodeEnum.Usd, label: "$ USD" },
  { value: CurrencyCodeEnum.Jpy, label: "¥ JPY" },
  { value: CurrencyCodeEnum.Inr, label: "₹ INR" },
  { value: CurrencyCodeEnum.Rub, label: "₽ RUB" },
  { value: CurrencyCodeEnum.Try, label: "₺ TRY" },
  { value: CurrencyCodeEnum.Zar, label: "R ZAR" },
  { value: CurrencyCodeEnum.Sgd, label: "S$ SGD" },
  { value: CurrencyCodeEnum.Chf, label: "CHF CHF" },
  { value: CurrencyCodeEnum.Dkk, label: "kr DKK" },
  { value: CurrencyCodeEnum.Isk, label: "kr ISK" },
  { value: CurrencyCodeEnum.Krw, label: "₩ KRW" },
  { value: CurrencyCodeEnum.Ngn, label: "₦ NGN" },
  { value: CurrencyCodeEnum.Nok, label: "kr NOK" },
  { value: CurrencyCodeEnum.Nzd, label: "NZ$ NZD" },
];

const TIMEZONE_OPTIONS = [
  // UTC time
  { label: "UTC", value: "UTC" },

  // North America
  { label: "Pacific Time (PST/PDT)", value: "America/Los_Angeles" },
  { label: "Mountain Time (MST/MDT)", value: "America/Denver" },
  { label: "Central Time (CST/CDT)", value: "America/Chicago" },
  { label: "Eastern Time (EST/EDT)", value: "America/New_York" },
  { label: "Hawaii Time (HST)", value: "Pacific/Honolulu" },
  { label: "Canada Central (CST/CDT)", value: "America/Winnipeg" },
  { label: "Canada Eastern (EST/EDT)", value: "America/Toronto" },

  // Europe
  { label: "London Time (GMT/BST)", value: "Europe/London" },
  { label: "Central European Time (CET/CEST)", value: "Europe/Paris" },
  { label: "Eastern European Time (EET/EEST)", value: "Europe/Athens" },
  { label: "Scandinavia (CET/CEST)", value: "Europe/Stockholm" },
  { label: "Switzerland (CET/CEST)", value: "Europe/Zurich" },
  { label: "Iceland (GMT)", value: "Atlantic/Reykjavik" },

  // Asia
  { label: "Japan Time (JST)", value: "Asia/Tokyo" },
  { label: "South Korea Time (KST)", value: "Asia/Seoul" },
  { label: "India Time (IST)", value: "Asia/Kolkata" },
  { label: "Singapore Time (SGT)", value: "Asia/Singapore" },
  { label: "Turkey Time (TRT)", value: "Europe/Istanbul" },

  // Africa
  { label: "South Africa Time (SAST)", value: "Africa/Johannesburg" },
  { label: "Nigeria Time (WAT)", value: "Africa/Lagos" },

  // Oceania
  { label: "Sydney Time (AEST/AEDT)", value: "Australia/Sydney" },
  { label: "New Zealand Time (NZST/NZDT)", value: "Pacific/Auckland" },

  // South America
  { label: "Brazil Time (BRT/BRST)", value: "America/Sao_Paulo" },

  // Russia
  { label: "Moscow Time (MSK)", value: "Europe/Moscow" },
];

function Team() {
  const params = useParams<{ clientId: string }>();
  const clientId = params.clientId;
  const { me } = useMe();
  const { client } = useClientById(clientId || "", {
    onlyFetchClient: true,
  });
  const [deleteMe, { loading: loadingDelete }] = useMutation(
    api.users.deleteMe
  );
  const role = me?.role || null;
  const navigate = useNavigate();

  const { data: permissionsData, loading } = useQuery<{
    getClientMembers?: ClientPermission[];
  }>(api.clientMembers.list, {
    notifyOnNetworkStatusChange: true,
    variables: { clientId },
    fetchPolicy: "cache-and-network",
  });

  // Modals
  const dispatch = useDispatch();
  const _showModal = compose(dispatch, show);

  const _onClickInviteMember = useCallback(
    () => _showModal("OrgMemberModal"),
    []
  );

  const _deleteMe = async () => {
    const userConfirmed = window.confirm(
      "Are you sure you want to delete your account? This action is irreversible."
    );

    // If the user confirms, then delete the user
    try {
      if (userConfirmed) {
        await deleteMe();

        alert(
          "Successfully deleted user account. If you have a minute, we'd love to hear about why Awaken didn't work for you. If you'd like to share your feedback, please email team@awaken.tax."
        );

        navigate("/landing");
      } else {
        // Optionally, handle the case where the user cancels the deletion
        console.log("Account deletion cancelled by the user.");
      }
    } catch (err) {
      if (err instanceof ApolloError) {
        alert(err.message);
      }
    }
  };

  const theme = useTheme();

  const permissions: ClientPermission[] =
    permissionsData?.getClientMembers || [];

  if (loading) {
    return <Loading />;
  }

  // Filter Modal
  return (
    <div
    // style={{
    //   padding: "1rem 2.5rem",
    // }}
    >
      <OrgMemberModal />
      <FiscalYearModal />

      <ButtonGroup
        w="100%"
        display="flex"
        justifyContent={"space-between"}
        alignItems="center"
      >
        <Heading color={theme.header} size="md">
          Permissions
        </Heading>
        <Box />
        {/* ^ leave this here since we might want to put button in-line later. */}
        <Button
          display="flex"
          justifyContent="center"
          alignItems="center"
          onClick={_onClickInviteMember}
          bgColor={theme.secondaryBackground}
          _hover={{ background: theme.ternaryBackground }}
          color={theme.header}
          marginTop="1rem"
          size="sm"
        >
          Add Member
        </Button>
      </ButtonGroup>
      <Text color={theme.text}>
        You can add people to help you label your transactions.
      </Text>

      <WhiteBox border={`1px solid ${theme.border}`} padding="0">
        <TableContainer>
          <Table variant="simple" border={`1px solid ${theme.border}`}>
            <Thead>
              <Tr>
                <Th borderColor={theme.border} color={theme.text}>
                  Name
                </Th>
                <Th borderColor={theme.border}>
                  <Center color={theme.text}>Email</Center>
                </Th>
                <Th borderColor={theme.border}>
                  <Center color={theme.text}>Activated Account</Center>
                </Th>
                <Th borderColor={theme.border}>
                  <Center borderColor={theme.border} color={theme.text}>
                    Actions
                  </Center>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {permissions.map((permission, i) => (
                <Member
                  numberOfPermissions={permissions.length}
                  isClientUser={client?.createdById === permission.userId}
                  isMe={me?.id === permission.userId}
                  isLast={permissions.length - 1 === i}
                  permission={permission}
                  key={permission.id}
                />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </WhiteBox>

      {role !== "accountant" && (
        <Box color={theme.text} w="100%" textAlign="right" marginTop="1rem">
          Are you an accountant?{" "}
          <a
            href="mailto:team@awaken.tax?subject=I'm an accountant and I'd like to use Awaken"
            style={{
              color: colors.primary,
              textDecoration: "underline",
              fontWeight: "bold",
              marginRight: "0.3rem",
            }}
          >
            Message us{" "}
            <Info message="You can use Awaken to collaborate with many different clients. Message us and we'd be happy to share more!" />
          </a>
        </Box>
      )}

      <Security />

      <Box
        style={{
          marginTop: "3rem",
          backgroundColor: theme.background,
          border: "1px solid " + theme.border,
          borderRadius: 5,
          padding: "1rem",
        }}
      >
        <Heading color={theme.header} flex={1} size="md" margin="0">
          Tax Settings
        </Heading>

        <br />

        <SwitchCountry />
        <br />
        <SwitchTaxAlgorithm />
        <br />
        <SwitchTimezone />
        <br />
        <SwitchCurrency />
        <br />
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginTop: "1rem",
          }}
        >
          <Box>
            <Text color={theme.header} fontSize="sm">
              Custom Fiscal Year
            </Text>

            <a
              href="https://help.awaken.tax/en/articles/11152756-setting-custom-fiscal-years"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, fontWeight: "bold" }}
            >
              Learn more{" "}
              <i className="fa-solid fa-arrow-up-right-from-square" />
            </a>
          </Box>

          {client?.hasCustomFiscalYear && (
            <Text marginLeft={"1.5rem"} marginTop={"5px"} color={theme.text}>
              {client.fiscalYearStart} to {client.fiscalYearEnd}
            </Text>
          )}

          <Button
            background={theme.secondaryBackground}
            _hover={{ background: theme.ternaryBackground }}
            color={theme.header}
            size="sm"
            style={{ marginLeft: "1.5rem" }}
            onClick={() =>
              dispatch(
                show("FiscalYearModal", {
                  defaultStartDate: client?.fiscalYearStart,
                  defaultEndDate: client?.fiscalYearEnd,
                })
              )
            }
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Box style={{ marginTop: 25 }}>
        <Button
          onClick={_deleteMe}
          isLoading={loadingDelete}
          style={{
            backgroundColor: colors.red50,
            color: colors.white,
          }}
        >
          Delete Account
        </Button>
      </Box>
    </div>
  );
}

const Security = () => {
  const { me } = useMe();
  const [hasTwoFactorEnabled, setHasTwoFactorEnabled] = useState(
    me?.hasTwoFactorAuth ?? false
  );
  const { clientId } = useParams<{ clientId: string }>();
  const dispatch = useDispatch();
  const toast = useMyToast();
  const [unenroll] = useMutation(api.users.unenrollTwoFactor);
  const [updateUser] = useMutation(api.users.update);
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  const { client } = useClientById(clientId || "", {
    onlyFetchClient: true,
  });
  const [updateClient] = useMutation(api.clients.update);
  const allowCustomerSupportAccess =
    client?.allowCustomerSupportAccess ?? false;

  const _setHasTwoFactorEnabled = async () => {
    // use firebase to determine if the user has 2FA enabled

    const user = auth.currentUser;

    if (!user) return false;

    const multiFactorUser = multiFactor(user);

    const enrolled = multiFactorUser.enrolledFactors.find(
      (i) => i.factorId === "phone" || i.factorId === "totp"
    );

    const hasTwoFactorAuth =
      !!enrolled &&
      (enrolled.factorId === "phone" || enrolled.factorId === "totp");

    setHasTwoFactorEnabled(hasTwoFactorAuth);

    // just send update to backend. doesn't rlly matter just easier to query this way with postgres
    // too see how many people are using this
    void updateUser({ variables: { hasTwoFactorAuth } });
  };

  useEffect(() => {
    _setHasTwoFactorEnabled();
  }, []);

  useEffect(() => {
    if (action === "update_email") {
      // empty update email
      updateUser({
        variables: {},
        refetchQueries: [api.users.me],
      });

      // delete the search param
      searchParams.delete("action");
    }
  }, [action]);

  const _setAllowCustomerSupportAccess = async (
    allowCustomerSupportAccess: boolean
  ) => {
    try {
      const variables: MutationUpdateClientArgs = {
        clientId: clientId || "",
        allowCustomerSupportAccess,
      };

      await updateClient({
        variables,
        refetchQueries: [api.clients.retrieve],
      });

      toast.show({
        status: "success",
        message: allowCustomerSupportAccess
          ? "Successfully enabled customer support access. You can turn this off at any time."
          : "Successfully disabled customer support access. No one will be able to view your data. You can turn this on at any time so we can help.",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const _onEnable2FA = async () => {
    toast.show({
      status: "success",
      message: "Successfully enabled two-factor authentication.",
    });

    await _setHasTwoFactorEnabled();
  };

  const _onDisable2FA = async () => {
    toast.show({
      status: "success",
      message: "Successfully disabled two-factor authentication.",
    });

    await _setHasTwoFactorEnabled();
  };

  const _unenrollIn2FA = async () => {
    const user = auth.currentUser;

    if (!user) {
      toast.show({
        message: "Error disabling two-factor authentication.",
        status: "error",
      });
      return;
    }

    // disable two factor with firebase
    const multiFactorUser = multiFactor(user);

    const enrolled = multiFactorUser.enrolledFactors.find(
      (i) => i.factorId === "phone" || i.factorId === "totp"
    );

    if (!enrolled) {
      toast.show({
        message: "Error disabling two-factor authentication.",
        status: "error",
      });
      return;
    }

    const unenrollResponse = await unenroll();
    const token = unenrollResponse.data?.unenrollTwoFactor;

    // resign with the token from the unenroll.
    await signInWithCustomToken(auth, token);

    _onDisable2FA();
  };

  const _changeEmail = async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.show({
        status: "error",
        message: "You must be logged in to change your email",
      });
      return;
    }

    // Get new email from user
    const newEmail = prompt("Enter your new email");

    if (!newEmail) return;

    try {
      dispatch(
        show("LoginModal", {
          flowType: "change_email",
          onSuccess: async () => {
            await verifyBeforeUpdateEmail(user, newEmail, {
              url: `${window.location.href}?action=update_email`,
            });

            toast.show({
              status: "success",
              message: "Please check your new email address for verification.",
            });
          },
        })
      );

      // Note: The email update in the backend should be handled after the user verifies their new email
      // You might want to set up an Auth trigger or handle this when the user logs in next time
    } catch (err) {
      console.error(err);

      // Handle specific Firebase errors
      if ((err as any)?.code === "auth/requires-recent-login") {
        toast.show({
          status: "error",
          message: "Please log out and log back in before changing your email.",
        });
      } else {
        toast.show({
          status: "error",
          message:
            (err as any)?.message || "Error updating email. Please try again.",
        });
      }
    }
  };

  const _enrollIn2FA = async (mfaType: "totp" | "sms" | null) => {
    // use firebase to add a 2 factor auth method to the user

    const user = auth.currentUser;

    if (!user) return;

    try {
      // enable 2 factor. make them login, then open up phone verification in enroll mode

      // dispatch(
      //   show("PhoneVerificationModal", {
      //     flowType: "enroll",
      //     onSuccess: _onEnable2FA,
      //   })
      // );

      dispatch(
        show("LoginModal", {
          flowType: "enroll",
          onSuccess: async () => {
            if (mfaType === "sms") {
              dispatch(
                show("PhoneVerificationModal", {
                  flowType: "enroll",
                  onSuccess: _onEnable2FA,
                })
              );
            } else {
              dispatch(
                show("TOTPModal", {
                  flowType: "enroll",
                  onSuccess: _onEnable2FA,
                })
              );
            }
          },
        })
      );

      return;
    } catch (err) {
      console.error(err);

      toast.show({
        status: "error",
        message:
          (err as any)?.message || "Error enabling two-factor authentication.",
      });
    }
  };

  return (
    <Box
      style={{
        marginTop: "3rem",
        backgroundColor: theme.background,
        border: "1px solid " + theme.border,
        borderRadius: 5,
        padding: "1rem",
      }}
    >
      <HStack>
        <Heading color={theme.header} flex={1} size="md" margin="0">
          Security{" "}
        </Heading>
        {!hasTwoFactorEnabled && (
          <StatusTag
            type="error"
            iconName="fa-solid fa-shield-halved"
            label="Please Enable 2FA"
          />
        )}
      </HStack>

      <br />

      {/* two factor authentication */}

      {/* make an interface that shows if two factor is enabled */}
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Text color={theme.text} style={{ flex: 1, maxWidth: 500 }}>
          Two-factor authentication{" "}
          {hasTwoFactorEnabled && (
            <i
              className="fa-sharp fa-check-circle"
              style={{ color: colors.primary }}
            />
          )}
        </Text>

        {hasTwoFactorEnabled ? (
          <Button
            background={theme.secondaryBackground}
            _hover={{ background: theme.ternaryBackground }}
            color={theme.header}
            size="sm"
            onClick={_unenrollIn2FA}
          >
            Disable
          </Button>
        ) : (
          <ActionSheet
            content={{ width: 300 }}
            commands={[
              {
                iconName: "fa-solid fa-qrcode",
                label: "Enroll in TOTP (recommended)",
                // infoMessage:
                //   "If you enable this, you will not be able to use our mobile app. Our website is mobile-friendly though, so you can always just go to https://awaken.tax to view your information on mobile.",
                onClick: () => _enrollIn2FA("totp"),
              },
              {
                iconName: "fa-solid fa-sms",
                label: "Enroll in SMS",
                onClick: () => _enrollIn2FA("sms"),
              },
            ]}
          >
            <Button
              background={theme.secondaryBackground}
              _hover={{ background: theme.ternaryBackground }}
              color={theme.header}
              size="sm"
            >
              Enable
            </Button>
          </ActionSheet>
        )}
      </Box>

      <br />

      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Text color={theme.text} style={{ flex: 1, maxWidth: 500 }}>
          Allow Awaken customer support to access your account.
        </Text>

        <Switch
          size="sm"
          onChange={(e) =>
            _setAllowCustomerSupportAccess(!allowCustomerSupportAccess)
          }
          isChecked={allowCustomerSupportAccess}
        />
      </Box>

      {/* <Box
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Text color={theme.text} style={{ flex: 1, maxWidth: 500 }}>
          Your email: {me?.email}
        </Text>

        <Button
          background={theme.secondaryBackground}
          _hover={{ background: theme.ternaryBackground }}
          color={theme.header}
          size="sm"
          onClick={_changeEmail}
        >
          Change Email
        </Button>
      </Box> */}
    </Box>
  );
};

const SwitchCountry = () => {
  const toast = useMyToast();
  const [updateClient] = useMutation(api.clients.update);
  const { clientId } = useParams<{ clientId: string }>();
  const { client } = useClientById(clientId, {
    onlyFetchClient: true,
  });
  const country = client?.country || CountryEnum.Us;
  const selectedOption = COUNTRIES.find(
    (a) => a.value.toLowerCase() === country.toLowerCase()
  );

  const _setOption = async (country: Maybe<CountryEnum>) => {
    try {
      await updateClient({
        variables: { clientId, country },
        refetchQueries: [api.transactions.countNotDone, api.clients.retrieve],
      });

      toast.show({
        status: "success",
        message: `Successfully updated country to ${country?.toUpperCase()}`,
      });
    } catch (err) {
      console.error(err);
      toast.show({
        status: "error",
        message:
          "Error updating country! " + (err as any)?.message ||
          "Unknown error.",
      });
    }
  };

  return (
    <Select
      options={COUNTRIES}
      label="Country"
      value={selectedOption}
      selectProps={{
        components: { IndicatorSeparator: null },
        onChange: (o: any) => _setOption(o.value as Maybe<CountryEnum>),
      }}
      containerStyle={{
        width: "15rem",
        marginBottom: 0,
      }}
    />
  );
};

const SwitchCurrency = () => {
  const toast = useMyToast();
  const [updateClient] = useMutation(api.clients.update);
  const { clientId } = useParams<{ clientId: string }>();
  const { client } = useClientById(clientId, {
    onlyFetchClient: true,
  });
  const currency = client?.currency || CurrencyCodeEnum.Usd;
  const selectedOption = CURRENCIES.find(
    (a) => a.value.toLowerCase() === currency.toLowerCase()
  );

  const _setOption = async (currency: Maybe<CurrencyCodeEnum>) => {
    try {
      await updateClient({
        variables: { clientId, currency },
        refetchQueries: [api.transactions.countNotDone, api.clients.retrieve],
      });

      toast.show({
        status: "success",
        message: `Successfully updated currency to ${currency?.toUpperCase()}`,
      });
    } catch (err) {
      console.error(err);
      const message = (err as any)?.message || "";
      toast.show({
        status: "error",
        message: "Error updating currency! " + message,
      });
    }
  };

  return (
    <Select
      options={CURRENCIES}
      value={selectedOption}
      label="Currency"
      selectProps={{
        components: { IndicatorSeparator: null },
        onChange: (o: any) => _setOption(o.value as Maybe<CurrencyCodeEnum>),
      }}
      containerStyle={{
        width: "15rem",
        marginBottom: 0,
      }}
    />
  );
};

const SwitchTaxAlgorithm = () => {
  const toast = useMyToast();
  const { me } = useMe();
  const theme = useTheme();

  const isSuperUser = me?.isSuperuser || false;
  const ALGOS = [
    { value: CostBasisAlgorithmEnum.Fifo, label: "FIFO" },
    { value: CostBasisAlgorithmEnum.Hifo, label: "HIFO" },
    { value: CostBasisAlgorithmEnum.Lifo, label: "LIFO" },
    { value: CostBasisAlgorithmEnum.Acb, label: "ACB" },
    { value: CostBasisAlgorithmEnum.SharePooling, label: "Share pooling" },
  ].filter(hasValue);

  const [updateAlgo] = useMutation(api.clients.updateClientCostBasisAlgorithm);
  const { clientId } = useParams<{ clientId: string }>();
  const { client } = useClientById(clientId, {
    onlyFetchClient: true,
  });
  const currentBasisAlgo =
    client?.costBasisAlgorithm || CostBasisAlgorithmEnum.Fifo;
  const selectedOption = ALGOS.find(
    (a) => a.value.toLowerCase() === currentBasisAlgo.toLowerCase()
  );

  const _setOption = async (algo: Maybe<CostBasisAlgorithmEnum>) => {
    try {
      await updateAlgo({
        variables: { clientId, costBasisAlgorithm: algo },
        refetchQueries: [api.transactions.countNotDone, api.clients.retrieve],
      });

      toast.show({
        status: "success",
        message: `Successfully updated tax algorithm to ${algo?.toUpperCase()}`,
      });
    } catch (err) {
      console.error(err);
      toast.show({
        status: "error",
        message: "Error updating tax algorithm!",
      });
    }
  };

  return (
    <HStack display="flex" margin="0" alignItems="center">
      {/* <StatusTag
        label="Tax Algorithm"
        infoMessage="If you used FIFO in previous years, you should use it this year too. The same goes for LIFO and HIFO. Not tax advice. Consult a tax specialist for more information."
        type="info"
        boxProps={{ border: "1px solid " + colors.lightBlue70 }}
        iconName="fa-sharp fa-info-circle"
      /> */}

      <Select
        options={ALGOS}
        value={selectedOption}
        label="Tax Algorithm"
        selectProps={{
          styles: {
            control: (provided) => ({
              ...provided,
              backgroundColor: theme.background,
              borderColor: theme.border,
            }),
            indicatorSeparator: (provider) => ({
              ...provider,
              display: "none",
            }),
            placeholder: (p) => ({
              ...p,
              color: theme.text,
            }),
            singleValue: (p) => ({ ...p, color: theme.header }),
          },
          onChange: (o: any) =>
            _setOption(o.value as Maybe<CostBasisAlgorithmEnum>),
        }}
        containerStyle={{
          width: "15rem",
          marginBottom: 0,
        }}
      />
    </HStack>
  );
};

const SwitchTimezone = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { client } = useClientById(clientId || "", {
    onlyFetchClient: true,
  });
  const toast = useMyToast();
  const [updateClient] = useMutation(api.clients.update);

  const timezone = client?.timezone;
  const timezoneOption = useMemo(
    () =>
      !timezone
        ? TIMEZONE_OPTIONS[0]
        : TIMEZONE_OPTIONS.find((option) => option.value === timezone) || null,
    [timezone]
  );

  const _updateTimezone = async (timezone: Maybe<string>) => {
    try {
      await updateClient({
        variables: { clientId, timezone },
      });

      toast.show({
        message:
          "Timezone successfully updated! When you pull reports and recalculate all your numbers will be in your specified timezone.",
        status: "success",
      });
    } catch (err) {
      console.error(err);
      toast.show({
        message: "Error updating timezone",
        status: "error",
      });
    }
  };

  return (
    <Select
      options={TIMEZONE_OPTIONS}
      value={timezoneOption}
      placeholder="Timezone"
      label="Timezone"
      selectProps={{
        onChange: (o: any) => _updateTimezone(o.value as Maybe<string>),
      }}
      containerStyle={{
        width: "15rem",
        marginBottom: 0,
      }}
    />
  );
};

export default Team;
